import { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  base_url,
  webServiceUrl,
} from "../../../components/apiCall/webserviceurl";

const API_BASE_URL = base_url;
const FEEDBACK_ENDPOINT = "/post-feedback";
const FEEDBACK_GET_ENDPOINT = "/get-post-feedback";

/* ---------------------------------------------------------------------
    RatingCard — a single 1–5 Likert question rendered as a row of
    star icons. Click a star to set the rating; hovering previews the
    value before it's committed.
  --------------------------------------------------------------------- */
function RatingCard({
  id,
  label,
  required,
  value,
  onChange,
  error,
  min = 1,
  max = 5,
  minLabel = "Poor",
  maxLabel = "Excellent",
}) {
  const [hoverValue, setHoverValue] = useState(0);

  const values = [];
  for (let v = min; v <= max; v++) values.push(v);

  const numericValue = Number(value) || 0;
  const activeValue = hoverValue || numericValue;

  return (
    <div className={`rc-field${error ? " rc-field--error" : ""}`} id={id}>
      <p className="rc-label">
        {label}
        {required && <span className="rc-required">&nbsp;*</span>}
      </p>

      <div className="rc-stars-row">
        <div
          className="rc-stars"
          role="radiogroup"
          aria-label={label}
          onMouseLeave={() => setHoverValue(0)}
        >
          {values.map((v) => {
            const selected = String(value) === String(v);
            const filled = v <= activeValue;
            return (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={`${v} star${v === 1 ? "" : "s"}`}
                className={`rc-star-btn${filled ? " rc-star-btn--filled" : ""}`}
                onMouseEnter={() => setHoverValue(v)}
                onFocus={() => setHoverValue(v)}
                onBlur={() => setHoverValue(0)}
                onClick={() => onChange(v)}
              >
                <svg viewBox="0 0 24 24" className="rc-star-icon">
                  <path d="M12 2.6l2.95 6.28 6.75.77-5.02 4.72 1.36 6.85L12 17.9l-6.04 3.32 1.36-6.85-5.02-4.72 6.75-.77L12 2.6z" />
                </svg>
              </button>
            );
          })}
        </div>
        <span
          className={`rc-star-value${numericValue ? " rc-star-value--set" : ""}`}
        >
          {numericValue ? `${numericValue}/${max}` : "Not rated"}
        </span>
      </div>

      <div className="rc-scale-labels">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>

      {error && <p className="rc-error">{error}</p>}
    </div>
  );
}

/* ---------------------------------------------------------------------
    PostSelect — a custom dropdown replacing the native <select>.
    Opens a floating, styled option panel under a button trigger, with
    outside-click/Escape to close and arrow-key + Enter navigation.
  --------------------------------------------------------------------- */
function PostSelect({
  id,
  options,
  value,
  onChange,
  error,
  placeholder = "Select...",
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target))
        setOpen(false);
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const idx = options.findIndex((o) => o === value);
    setHighlight(idx >= 0 ? idx : 0);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = (opt) => {
    onChange(opt);
    setOpen(false);
  };

  const handleTriggerKeyDown = (e) => {
    if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
      e.preventDefault();
      setOpen(true);
    }
  };

  const handlePanelKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (options[highlight] !== undefined) commit(options[highlight]);
    }
  };

  return (
    <div
      className="post-select-wrap"
      ref={rootRef}
      onKeyDown={open ? handlePanelKeyDown : handleTriggerKeyDown}
    >
      <button
        type="button"
        id={id}
        className={`post-select-trigger${error ? " post-select-trigger--error" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span
          className={`post-select-value${!value ? " post-select-placeholder" : ""}`}
        >
          {value || placeholder}
        </span>
        <svg
          className={`post-select-chevron${open ? " post-select-chevron--open" : ""}`}
          width="15"
          height="15"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <ul
          className="post-dropdown-panel"
          role="listbox"
          aria-label={id}
          tabIndex={-1}
        >
          {options.length === 0 && (
            <li className="post-dropdown-empty">No options</li>
          )}
          {options.map((opt, i) => (
            <li
              key={opt}
              role="option"
              aria-selected={opt === value}
              className={`post-dropdown-item${opt === value ? " post-dropdown-item--selected" : ""}${i === highlight ? " post-dropdown-item--highlighted" : ""
                }`}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => commit(opt)}
            >
              {opt === value && (
                <svg
                  className="post-dropdown-check"
                  width="14"
                  height="14"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M4 10.2l3.2 3.2L16 5"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              <span>{opt}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------
    FormInput — text / email / date / select fields used in the
    "Participant & Training Details" section.
  --------------------------------------------------------------------- */
function FormInput({
  id,
  type,
  label,
  required,
  placeholder,
  options,
  value,
  onChange,
  error,
}) {
  const commonLabel = (
    <p className="rc-label">
      {label}
      {required && <span className="rc-required">&nbsp;*</span>}
    </p>
  );

  if (type === "checkbox") {
    return (
      <div>
        {commonLabel}
        <label className="post-checkbox-row">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span>{placeholder}</span>
        </label>
      </div>
    );
  }

  if (type === "select") {
    return (
      <div>
        {commonLabel}
        <PostSelect
          id={id}
          options={options}
          value={value}
          onChange={onChange}
          error={error}
        />
        {error && <p className="rc-error">{error}</p>}
      </div>
    );
  }

  return (
    <div>
      {commonLabel}
      <input
        id={id}
        className="post-input"
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {error && <p className="rc-error">{error}</p>}
    </div>
  );
}

function PreviewStyles() {
  return (
    <style>{`
* {
  box-sizing: border-box;
}

.rc-field {
  display: flex;
  flex-direction: column;
}

.rc-label {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.5;
  margin: 0 0 14px;
}

.rc-required {
  color: #e11d48;
}

/* ---- Star rating ---- */
.rc-stars-row {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.rc-stars {
  display: flex;
  gap: 4px;
}

.rc-star-btn {
  background: none;
  border: none;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
  transition: transform 0.12s ease, background-color 0.15s ease;
}

.rc-star-btn:hover,
.rc-star-btn:focus-visible {
  transform: scale(1.14);
  background: #f0fdf4;
}

.rc-star-btn:focus-visible {
  outline: 2px solid #16a34a;
  outline-offset: 1px;
}

.rc-star-btn:active {
  transform: scale(0.94);
}

.rc-star-icon {
  width: 30px;
  height: 30px;
  fill: #e5e7eb;
  stroke: #d1d5db;
  stroke-width: 1;
  transition: fill 0.15s ease, stroke 0.15s ease, filter 0.15s ease;
}

.rc-star-btn--filled .rc-star-icon {
  fill: #fbbf24;
  stroke: #f59e0b;
  filter: drop-shadow(0 2px 4px rgba(245, 158, 11, 0.35));
}

.rc-star-value {
  font-size: 12.5px;
  font-weight: 700;
  color: #9ca3af;
  background: #f3f4f6;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
}

.rc-star-value--set {
  color: #92660a;
  background: #fef3c7;
}

.rc-scale-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  max-width: 210px;
  font-size: 12px;
  color: #9ca3af;
}

.rc-error {
  color: #e11d48;
  font-size: 12.5px;
  margin: 8px 0 0;
}

.rc-field--error .rc-star-icon {
  stroke: #fda4af;
}

/* ---- Custom select dropdown ---- */
.post-select-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.post-select-trigger {
  width: 100%;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1.5px solid #e2e5e9;
  background: #fff;
  font-size: 13.5px;
  font-family: inherit;
  color: #1f2937;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.post-select-trigger:hover {
  border-color: #bcd9c6;
}

.post-select-trigger:focus-visible,
.post-select-trigger[aria-expanded="true"] {
  outline: none;
  border-color: #16a34a;
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.14);
}

.post-select-trigger--error {
  border-color: #fda4af;
}

.post-select-value {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.post-select-placeholder {
  color: #9ca3af;
}

.post-select-chevron {
  flex-shrink: 0;
  color: #9ca3af;
  transition: transform 0.2s ease, color 0.15s ease;
}

.post-select-chevron--open {
  transform: rotate(180deg);
  color: #16a34a;
}

.post-dropdown-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  margin: 0;
  padding: 6px;
  list-style: none;
  background: #fff;
  border: 1.5px solid #e2e5e9;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(17, 24, 39, 0.14), 0 2px 8px rgba(17, 24, 39, 0.06);
  max-height: 220px;
  overflow-y: auto;
  z-index: 40;
  animation: post-dropdown-in 0.16s ease;
}

.post-dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 10px;
  border-radius: 8px;
  font-size: 13.5px;
  color: #374151;
  cursor: pointer;
  transition: background-color 0.12s ease, color 0.12s ease;
}

.post-dropdown-item--highlighted {
  background: #f0fdf4;
}

.post-dropdown-item--selected {
  background: #e6f4ea;
  color: #166534;
  font-weight: 600;
}

.post-dropdown-check {
  flex-shrink: 0;
  color: #16a34a;
}

.post-dropdown-empty {
  padding: 9px 10px;
  font-size: 13px;
  color: #9ca3af;
}

@keyframes post-dropdown-in {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ---------------------------------------------------------------- */
/* Page shell                                                        */
/* ---------------------------------------------------------------- */

.post-page {
  min-height: 100vh;
  background: #f6f9f7;
  padding: 28px 16px 40px;
  font-family: "Inter", "Segoe UI", Arial, sans-serif;
  color: #1f2937;
}

.post-content {
  // max-width: 760px;
  margin: 0 auto;
}

/* ---------------------------------------------------------------- */
/* Header                                                             */
/* ---------------------------------------------------------------- */

.post-feedback-header-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(17, 24, 39, 0.06);
  padding: 28px 28px 24px;
  margin-bottom: 20px;
}

.post-feedback-header-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 6px;
}

.post-header-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
  flex-shrink: 0;
}

.post-feedback-eyebrow {
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #16a34a;
  margin: 0 0 2px;
}

.post-feedback-title {
  font-size: 20px;
  font-weight: 800;
  color: #111827;
  margin: 0;
  line-height: 1.3;
}

.post-feedback-subtitle {
  font-size: 13.5px;
  color: #6b7280;
  margin: 12px 0 0;
  line-height: 1.6;
}

.post-loading-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #166534;
  background: #f0fdf4;
  border: 1px solid #d3f0dd;
  border-radius: 10px;
  padding: 10px 14px;
  margin-top: 16px;
}

.post-loaded-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #92660a;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 10px 14px;
  margin-top: 16px;
}

/* Progress bar */
.post-progress-wrap {
  margin-top: 20px;
}

.post-progress-track {
  height: 8px;
  border-radius: 999px;
  background: #eef2f0;
  overflow: hidden;
}

.post-progress-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #16a34a, #22c55e);
  transition: width 0.25s ease;
}

.post-progress-label {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #9ca3af;
  margin-top: 7px;
}

.post-progress-label strong {
  color: #16a34a;
  font-weight: 700;
}

/* ---------------------------------------------------------------- */
/* Sections                                                          */
/* ---------------------------------------------------------------- */

.post-section {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(17, 24, 39, 0.06);
  padding: 26px 28px 28px;
  margin-bottom: 20px;
}

.post-section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 18px;
  margin-bottom: 22px;
  border-bottom: 1px solid #eef1ef;
}

.post-section-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #e6f4ea;
  color: #16a34a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.post-section-title {
  font-size: 15.5px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.post-section-sub {
  font-size: 12px;
  color: #9ca3af;
  margin: 2px 0 0;
}

/* Metadata field grid */
.post-field-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 22px 24px;
}

.post-field-grid--span2 {
  grid-column: span 2;
}

/* Rating question list — full width, generous spacing so long question
   text never fights for space with the answer scale. */
.post-rating-list {
  display: flex;
  flex-direction: column;
  gap: 26px;
}

.post-rating-list .rc-field + .rc-field {
  padding-top: 26px;
  border-top: 1px dashed #eef1ef;
}

/* Rating question grid — two RatingCards per row for the grouped
   rating sections, so a long block of Likert questions reads as a
   compact grid instead of one long scrolling column. */
.post-rating-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 26px 24px;
}

.post-input {
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1.5px solid #e2e5e9;
  font-size: 13.5px;
  font-family: inherit;
  color: #1f2937;
  background: #fff;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.post-input:focus {
  border-color: #16a34a;
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.12);
}

.post-checkbox-row {
  display: flex;
  gap: 8px;
  font-size: 13px;
  color: #374151;
  align-items: flex-start;
}

/* ---------------------------------------------------------------- */
/* Suggestions textarea                                              */
/* ---------------------------------------------------------------- */

.post-textarea {
  width: 100%;
  min-height: 100px;
  resize: vertical;
  border: 1.5px solid #e2e5e9;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 13.5px;
  font-family: inherit;
  color: #1f2937;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.post-textarea:focus {
  border-color: #16a34a;
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.12);
}

.post-textarea-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
}

.post-char-count {
  font-size: 11px;
  color: #9ca3af;
}

/* ---------------------------------------------------------------- */
/* Footer / submit — sticks to the bottom of the viewport so it's     */
/* always reachable on a long form.                                  */
/* ---------------------------------------------------------------- */

.post-feedback-footer {
  position: sticky;
  bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  background: #f0fdf4;
  border: 1px solid #d3f0dd;
  border-radius: 14px;
  padding: 16px 22px;
  box-shadow: 0 8px 24px rgba(17, 24, 39, 0.1);
}

.post-copy-note {
  font-size: 13px;
  color: #166534;
  margin: 0;
}

.post-submit-row {
  display: flex;
  align-items: center;
  gap: 18px;
}

.post-submit-btn {
  background: #16a34a;
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 11px 26px;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: box-shadow 0.15s ease, transform 0.1s ease, background 0.15s ease;
}

.post-submit-btn:hover {
  background: #128a3e;
  box-shadow: 0 6px 16px rgba(22, 163, 74, 0.35);
}

.post-submit-btn:active {
  transform: translateY(1px);
}

.post-submit-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  box-shadow: none;
}

.post-clear-btn {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.post-clear-btn:hover {
  text-decoration: underline;
}

.post-clear-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  text-decoration: none;
}

.post-submit-error {
  color: #e11d48;
  font-size: 12.5px;
  margin: 0;
  width: 100%;
}

/* ---------------------------------------------------------------- */
/* Success state                                                     */
/* ---------------------------------------------------------------- */

.post-success-card {
  max-width: 480px;
  margin: 80px auto 0;
  background: #fff;
  border-radius: 16px;
  padding: 40px 32px;
  text-align: center;
  box-shadow: 0 2px 16px rgba(17, 24, 39, 0.08);
}

.post-success-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #16a34a;
  color: #fff;
  font-size: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;
}

.post-success-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 8px;
  color: #111827;
}

.post-success-text {
  font-size: 13.5px;
  color: #6b7280;
  margin: 0 0 24px;
  line-height: 1.6;
}

@media (max-width: 620px) {
  .post-feedback-header-card,
  .post-section {
    padding: 20px 18px 22px;
  }
  .post-field-grid {
    grid-template-columns: 1fr;
  }
  .post-field-grid--span2 {
    grid-column: span 1;
  }
  .post-rating-grid {
    grid-template-columns: 1fr;
  }
  .rc-star-icon {
    width: 26px;
    height: 26px;
  }
  .post-feedback-footer {
    position: static;
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
  .post-submit-row {
    justify-content: center;
  }
  .post-submit-btn {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .rc-star-btn,
  .rc-star-icon,
  .post-input,
  .post-textarea,
  .post-submit-btn,
  .post-progress-fill,
  .post-select-chevron,
  .post-dropdown-panel {
    transition: none;
    animation: none;
  }
}
  `}</style>
  );
}

// Every 1–5 rating question, described as data so <RatingCard /> renders
// all of them consistently (label, key, and whether it's required).
const ratingQuestions = [
  {
    key: "learningObjectives",
    label:
      "To what extent did the training program meet your learning objectives?",
  },
  {
    key: "concernsAddressed",
    label:
      "How effectively did the training program address your concerns and questions?",
  },
  {
    key: "instructorEffectiveness",
    label:
      "How would you rate the instructor's effectiveness in delivering the training?",
  },
  {
    key: "engagingInteractive",
    label: "How engaging and interactive was the training program?",
  },
  {
    key: "understandingEnhanced",
    label:
      "To what extent did the training program enhance your understanding of Sustainable Development/Robotics/IOT concept related to your domain?",
  },
  {
    key: "handsOnEngaging",
    label: "How engaging were the hands-on activities and projects?",
  },
  {
    key: "overallKnowledge",
    label:
      "How would you rate your overall knowledge of Emerging Technology/Robotics & IOT/ AI & Coding related to your domain post sessions?",
  },
  {
    key: "perspectiveChanged",
    label:
      "How has this training program changed your perspective on the potential impact of Sustainable Development/Robotics/IOT on society?",
  },
  { key: "doubtsAddressed", label: "Were your doubts and queries addressed?" },
  {
    key: "topOverallExperience",
    label: "How would you rate your overall experience with the TOP sessions?",
  },
  {
    key: "topProblemSolving",
    label:
      "How effectively did the TOP sessions enhance your problem-solving skills?",
  },
  {
    key: "topHandsOn",
    label:
      "Did the TOP sessions provide enough hands-on experience for project development?",
  },
  { key: "labVisit", label: "How was your experience in lab visit?" },
  {
    key: "recommendLikelihood",
    label: "How likely are you to recommend this training program to others?",
  },
];

const ratingQuestionsByKey = Object.fromEntries(
  ratingQuestions.map((q) => [q.key, q]),
);

// Groups the 14 rating questions into readable sections instead of one
// long undifferentiated wall of cards.
const ratingSections = [
  {
    title: "Training Program Feedback",
    sub: "Content, delivery, and instructor effectiveness",
    icon: "🎓",
    keys: [
      "learningObjectives",
      "concernsAddressed",
      "instructorEffectiveness",
      "engagingInteractive",
      "understandingEnhanced",
      "handsOnEngaging",
      "overallKnowledge",
      "perspectiveChanged",
      "doubtsAddressed",
    ],
  },
  {
    title: "TOP Sessions Feedback",
    sub: "Hands-on project work and lab experience",
    icon: "🛠️",
    keys: [
      "topOverallExperience",
      "topProblemSolving",
      "topHandsOn",
      "labVisit",
    ],
  },
];

const initialState = {
  emailRecord: false,
  email: "",
  trainingStartDate: "",
  nameOfKV: "",
  trainingEndDate: "",
  fullName: "",
  classesTaught: "",
  suggestions: "",
  ...Object.fromEntries(ratingQuestions.map((q) => [q.key, ""])),
};

const SUGGESTIONS_MAX_LENGTH = 500;

// Every field that counts toward "required" — used for both validation
// and the progress bar so the two stay in sync.
const requiredKeys = [
  "email",
  "trainingStartDate",
  "trainingEndDate",
  "fullName",
  "classesTaught",
  ...ratingQuestions.map((q) => q.key),
  "suggestions",
];

function validate(fields) {
  const errors = {};
  // if (!fields.email.trim()) errors.email = "Email is required.";
  // if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email))
  //   errors.email = "Enter a valid email address.";
  // if (!fields.trainingStartDate)
  //   errors.trainingStartDate = "Start date is required.";
  // if (!fields.trainingEndDate) errors.trainingEndDate = "End date is required.";
  // if (!fields.fullName.trim()) errors.fullName = "Full name is required.";
  if (!fields.classesTaught) errors.classesTaught = "Please select an option.";
  ratingQuestions.forEach((q) => {
    if (!fields[q.key]) errors[q.key] = "Please select a rating.";
  });
  if (!fields.suggestions.trim())
    errors.suggestions = "This field is required.";
  return errors;
}

// Maps the UI's "classesTaught" select option to the short code the
// backend expects (e.g. "Grades 6 to 12" -> "6-12").
function mapClassesTaughtToCode(classesTaught) {
  const map = {
    "Grades 6 to 12": "6-12",
    "Grades 9 to 12": "9-12",
  };
  return map[classesTaught] || classesTaught;
}

// Reverse of mapClassesTaughtToCode — turns the backend's short code
// (e.g. "6-12") back into the label the select control displays.
function mapCodeToClassesTaught(code) {
  const map = {
    "6-12": "Grades 6 to 12",
    "9-12": "Grades 9 to 12",
  };
  return map[code] || code || "";
}

// Builds the exact payload shape the /post-feedback API expects.
function buildFeedbackPayload(fields) {
  return {
    classes_taught: mapClassesTaughtToCode(fields.classesTaught),
    suggestions: fields.suggestions,
    ratings: ratingQuestions.map((q) => ({
      question_code: q.key,
      rating: Number(fields[q.key]),
    })),
  };
}

// Maps the /get-post-feedback API response shape into the flat `fields`
// state the form uses. Only overwrites keys the response actually has
// data for — every other field (fullName, training dates, etc., which
// the GET response doesn't return) is left untouched.
function mapExistingFeedbackToFields(responseData, currentFields) {
  if (!responseData) return currentFields;

  const { submission, ratings } = responseData;
  const next = { ...currentFields };

  if (submission) {
    if (submission.classes_taught != null) {
      next.classesTaught = mapCodeToClassesTaught(submission.classes_taught);
    }
    if (submission.suggestions != null) {
      next.suggestions = submission.suggestions;
    }
  }

  if (ratings) {
    ratingQuestions.forEach((q) => {
      const ratingValue = ratings[q.key];
      if (ratingValue !== undefined && ratingValue !== null) {
        next[q.key] = String(ratingValue);
      }
    });
  }

  return next;
}

export default function KVSRegistrationFormRegistrationForm() {
  const [fields, setFields] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const name = localStorage.getItem("name");
  const grades = localStorage.getItem("grades");

  // Existing-feedback lookup (GET /get-post-feedback). This becomes
  // populated once a submission already exists (e.g. right after a
  // POST submit); if nothing exists yet, the form just stays in its
  // normal empty/default state.
  const [isLoadingExisting, setIsLoadingExisting] = useState(true);
  const [hasExistingFeedback, setHasExistingFeedback] = useState(false);
  const [existingLoadError, setExistingLoadError] = useState("");

  console.log(hasExistingFeedback, "setHasExistingFeedback");

  const fetchExistingFeedback = async () => {
    setIsLoadingExisting(true);
    setExistingLoadError("");
    try {
      const token = localStorage.getItem("token");
      const getUrl =
        webServiceUrl.get_post_feedback ||
        `${API_BASE_URL}${FEEDBACK_GET_ENDPOINT}`;

      const res = await axios.get(getUrl, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const responseData = res?.data?.data;

      if (res?.data?.success && responseData) {
        setHasExistingFeedback(true);
        setFields((prev) => mapExistingFeedbackToFields(responseData, prev));
      } else {
        // No existing data — leave the form exactly as it was.
        setHasExistingFeedback(false);
      }
    } catch (err) {
      // 404 / no record yet is expected for a brand-new form — treat
      // it the same as "no data", not as an error banner.
      if (err?.response?.status !== 404) {
        console.error("Failed to load existing feedback:", err);
        setExistingLoadError(
          err?.response?.data?.message ||
          err?.message ||
          "Could not load your previous responses.",
        );
      }
      setHasExistingFeedback(false);
    } finally {
      setIsLoadingExisting(false);
    }
  };

  // Load any existing feedback once on mount.
  useEffect(() => {
    fetchExistingFeedback();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key) => (value) => {
    setFields((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  useEffect(() => {
    setFields((f) => ({ ...f, classesTaught: grades }));
  }, [grades]);

  const answeredCount = requiredKeys.filter((k) => {
    const v = fields[k];
    return typeof v === "string" ? v.trim().length > 0 : !!v;
  }).length;
  const totalCount = requiredKeys.length;
  const progressPct = Math.round((answeredCount / totalCount) * 100);

  const handleSubmit = async () => {
    const errs = validate(fields);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const firstKey = Object.keys(errs)[0];
      const el = document.getElementById(firstKey);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      console.log(errs, "presssings");
      return;
    }

    setSubmitError("");
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      console.log(token, "pressings");
      const payload = buildFeedbackPayload(fields);

      await axios.post(webServiceUrl.post_traning, payload, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      await fetchExistingFeedback();
      setSubmitted(true);

      // Refresh from the GET endpoint so the latest saved values (as
      // the backend now has them) are reflected if the user comes
      // back to this form again.
    } catch (err) {
      console.error("Feedback submission failed:", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong while submitting your feedback. Please try again.";
      setSubmitError(message);
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = async () => {
    await fetchExistingFeedback();
    // setFields(initialState);
    setErrors({});
    setSubmitted(false);
    setSubmitError("");
  };

  if (submitted) {
    return (
      <div className="post-page">
        <PreviewStyles />
        <div className="post-success-card">
          <div className="post-success-icon">✓</div>
          <h2 className="post-success-title">Feedback Submitted!</h2>
          <p className="post-success-text">
            A copy of your responses will be emailed to support@ihfc.co.in.
          </p>
          <button className="post-clear-btn" onClick={handleClear}>
            Thank you for submission
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="post-page">
      <PreviewStyles />
      <div className="post-content">
        {/* Header + progress */}
        <div className="post-feedback-header-card">
          <div className="post-feedback-header-row">
            <div className="post-header-icon">📝</div>
            <div>
              <p className="post-feedback-eyebrow">Post-Training Feedback</p>
              <h1 className="post-feedback-title">
                post STEM Teacher Training Program
              </h1>
            </div>
          </div>
          <p className="post-feedback-subtitle">
            We're eager to hear your thoughts on the recent teacher training
            program. This feedback helps us improve future sessions for
            everyone.
          </p>

          {isLoadingExisting && (
            <div className="post-loading-banner">
              <span>⏳</span>
              <span>Checking for your previous responses...</span>
            </div>
          )}

          {/* {!isLoadingExisting && hasExistingFeedback && (
            <div className="post-loaded-banner">
              <span>ℹ️</span>
              <span>
                We found your previous responses and pre-filled them below —
                feel free to review and update before submitting again.
              </span>
            </div>
          )} */}

          {!isLoadingExisting && existingLoadError && (
            <div className="post-loaded-banner">
              <span>⚠️</span>
              <span>{existingLoadError}</span>
            </div>
          )}

          <div className="post-progress-wrap">
            <div className="post-progress-track">
              <div
                className="post-progress-fill"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="post-progress-label">
              <span>
                <strong>{answeredCount}</strong> of {totalCount} questions
                answered
              </span>
              <span>{progressPct}%</span>
            </div>
          </div>
        </div>

        {/* Participant & training details */}
        <section className="post-section">
          <div className="post-section-header">
            <span className="post-section-icon">🧑‍🏫</span>
            <div>
              <h2 className="post-section-title">
                Participant &amp; Training Details
              </h2>
              <p className="post-section-sub">
                A few details so we can match this feedback to the right batch
              </p>
            </div>
          </div>

          <div className="post-field-grid">
            {/* <div className="post-field-grid--span2">
              <FormInput
                id="email"
                type="email"
                label="Email"
                required
                placeholder="Your answer"
                value={fields.email}
                onChange={set("email")}
                error={errors.email}
              />
            </div> */}

            <FormInput
              id="fullName"
              type="text"
              label="Full name as per service record"
              required
              placeholder="Your answer"
              // value={fields.fullName}
              value={name}
            // onChange={set("fullName")}
            // error={errors.fullName}
            />

            <FormInput
              id="classesTaught"
              type="select"
              label="Which classes/grades did you teach?"
              required
              options={[
                "Grades 6 to 9",
                "Grades 11 to 12",
                "Diploma",
                "Graduate",
                "Post Graduate",
                "PhD",
              ]}
              value={fields.classesTaught}
              onChange={set("classesTaught")}
              error={errors.classesTaught}
            />

            {/* <FormInput
              id="trainingStartDate"
              type="date"
              label="Date of training: start"
              required
              value={fields.trainingStartDate}
              onChange={set("trainingStartDate")}
              error={errors.trainingStartDate}
            /> */}
            {/* 
            <FormInput
              id="trainingEndDate"
              type="date"
              label="Date of training: end"
              required
              value={fields.trainingEndDate}
              onChange={set("trainingEndDate")}
              error={errors.trainingEndDate}
            /> */}
          </div>
        </section>

        {/* Rating sections */}
        {ratingSections.map((section) => (
          <section className="post-section" key={section.title}>
            <div className="post-section-header">
              <span className="post-section-icon">{section.icon}</span>
              <div>
                <h2 className="post-section-title">{section.title}</h2>
                <p className="post-section-sub">{section.sub}</p>
              </div>
            </div>

            <div className="post-rating-grid">
              {section.keys.map((key) => {
                const q = ratingQuestionsByKey[key];
                return (
                  <RatingCard
                    key={key}
                    id={q.key}
                    label={q.label}
                    required
                    value={fields[q.key]}
                    onChange={set(q.key)}
                    error={errors[q.key]}
                  />
                );
              })}
            </div>
          </section>
        ))}

        {/* Final thoughts */}
        <section className="post-section">
          <div className="post-section-header">
            <span className="post-section-icon">💬</span>
            <div>
              <h2 className="post-section-title">Final Thoughts</h2>
              <p className="post-section-sub">
                Your overall recommendation and any suggestions
              </p>
            </div>
          </div>

          <div className="post-rating-list">
            <RatingCard
              id="recommendLikelihood"
              label={ratingQuestionsByKey.recommendLikelihood.label}
              required
              value={fields.recommendLikelihood}
              onChange={set("recommendLikelihood")}
              error={errors.recommendLikelihood}
              minLabel="Unlikely"
              maxLabel="Very likely"
            />

            <div>
              <p className="rc-label">
                Any further suggestions you would like to convey
                <span className="rc-required">&nbsp;*</span>
              </p>
              <textarea
                id="suggestions"
                className="post-textarea"
                placeholder="Type your suggestions here..."
                maxLength={SUGGESTIONS_MAX_LENGTH}
                value={fields.suggestions}
                onChange={(e) => set("suggestions")(e.target.value)}
              />
              <div className="post-textarea-footer">
                <span className="post-char-count">
                  {fields.suggestions.length}/{SUGGESTIONS_MAX_LENGTH}
                </span>
              </div>
              {errors.suggestions && (
                <p className="rc-error">{errors.suggestions}</p>
              )}
            </div>
          </div>
        </section>

        {/* Submit */}
        <div className="post-feedback-footer">
          <p className="post-copy-note">
            📩 A copy of your responses will be emailed to support@ihfc.co.in
          </p>
          {submitError && <p className="post-submit-error">{submitError}</p>}
          {/* {!submitted && ( */}
          {!hasExistingFeedback && (
            <div className="post-submit-row">
              <button
                className="post-clear-btn"
                onClick={handleClear}
                disabled={isSubmitting}
              >
                Clear form
              </button>
              <button
                className="post-submit-btn"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Feedback ➤"}
              </button>
            </div>
          )}
          {/* )} */}
        </div>
      </div>
    </div>
  );
}
